import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, BookOpen, FileText, LayoutDashboard, Menu, Settings } from "lucide-react";
import RouteFocusManager from "./RouteFocusManager";
import Dialog from "../ui/Dialog";
import StartDecisionReview, { pullRequestProblem } from "../reviews/StartDecisionReview";
import { ReviewLaunchContext } from "./reviewLaunch";
import "./shell.css";

const EXPERIMENTAL = process.env.REACT_APP_EXPERIMENTAL_TASKS === "true";

const NAV = [
  { kind: "workspace", href: "/workspace", label: "Overview" },
  { kind: "reviews", href: "/reviews", label: "Reviews" },
  { kind: "guides", href: "/brain", label: "Product Rules" },
  { kind: "activity", href: "/activity", label: "Activity" },
  ...(EXPERIMENTAL ? [{ kind: "tasks", href: "/tasks", label: "Tasks" }] : []),
];

function Icon({ name }) {
  const props = { size: 16, "aria-hidden": true };
  if (name === "workspace") return <LayoutDashboard {...props} />;
  if (name === "guides") return <BookOpen {...props} />;
  if (name === "activity") return <Activity {...props} />;
  if (name === "tasks") return <FileText {...props} />;
  if (name === "reviews") return <FileText {...props} />;
  return <FileText {...props} />;
}

function navCurrent(area, item) {
  if (item.kind === "guides" && String(area).startsWith("guide")) return "page";
  if (item.kind === "tasks" && ["tasks", "task", "task-new", "repository"].includes(area)) return "page";
  if (item.kind === "reviews" && ["reviews", "decision", "start"].includes(area)) return "page";
  if (item.kind === "workspace" && area === "workspace") return "page";
  if (item.kind === "activity" && area === "activity") return "page";
  if (item.kind === area) return "page";
  return undefined;
}

function accountLabel(user) {
  const named = String(user?.displayName || user?.name || "").trim();
  if (named && !named.includes("@") && named.length <= 18 && !/\d{3,}/.test(named)) return named.split(/\s+/)[0];
  const prefix = String(user?.email || "").split("@")[0];
  if (/^[A-Za-z]{2,16}$/.test(prefix)) return prefix;
  return "Account";
}

function sectionHref(path, repositoryId) {
  if (!repositoryId) return path;
  return `${path}?repository=${encodeURIComponent(repositoryId)}`;
}

function repoLabel(repo) {
  if (!repo) return "Choose a codebase";
  return repo.owner && repo.name ? `${repo.owner}/${repo.name}` : repo.name || "Codebase";
}

function accountInitial(user) {
  const label = accountLabel(user);
  return label === "Account" ? "R" : label.charAt(0).toUpperCase();
}

export default function AppShell({
  area = "reviews",
  pathname = "/",
  user,
  workspaceName = "",
  repositories = [],
  repositoryId = "",
  allowAllCodebases = false,
  onRepository,
  mutateAllowed = true,
  onLogout,
  children,
}) {
  const navigate = useNavigate();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewSeed, setReviewSeed] = useState("");
  const [prUrl, setPrUrl] = useState("");
  const [prError, setPrError] = useState("");
  const [navOpen, setNavOpen] = useState(false);
  const [repoOpen, setRepoOpen] = useState(false);
  const [repoQuery, setRepoQuery] = useState("");
  const repoButtonRef = React.useRef(null);
  const repoMenuRef = React.useRef(null);
  const reviewButtonRef = React.useRef(null);

  const closeReview = useCallback(() => setReviewOpen(false), []);
  const openReview = useCallback((url = "") => {
    setReviewSeed(String(url || ""));
    setReviewOpen(true);
  }, []);
  const closeRepos = useCallback(() => {
    setRepoOpen(false);
    setRepoQuery("");
  }, []);
  const selectedRepo = repositories.find((repo) => String(repo._id) === String(repositoryId));
  const visibleRepos = repositories.filter((repo) => repoLabel(repo).toLowerCase().includes(repoQuery.trim().toLowerCase()));
  const selectorLabel = selectedRepo
    ? repoLabel(selectedRepo)
    : allowAllCodebases
      ? `${workspaceName || "Workspace"} · All codebases`
      : "Choose a codebase";

  function chooseRepository(nextId) {
    closeRepos();
    onRepository?.(nextId);
  }

  function submitLauncher(event) {
    event.preventDefault();
    const problem = prUrl.trim() ? pullRequestProblem(prUrl) : "";
    if (problem) {
      setPrError(problem);
      return;
    }
    setPrError("");
    openReview(prUrl.trim());
  }

  useEffect(() => {
    if (!repoOpen) return undefined;
    function onKey(event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeRepos();
        repoButtonRef.current?.focus();
      }
    }
    function onPointer(event) {
      if (repoMenuRef.current?.contains(event.target) || repoButtonRef.current?.contains(event.target)) return;
      closeRepos();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [repoOpen, closeRepos]);

  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  return (
    <ReviewLaunchContext.Provider value={{ open: openReview }}>
      <div className="rr-shell">
        <a className="rr-skip" href="#main-content">Skip to content</a>
        <header className="rr-topbar">
          <button type="button" className="rr-button secondary rr-nav-toggle" aria-expanded={navOpen} aria-controls="primary-navigation" onClick={() => setNavOpen((open) => !open)}>
            <Menu size={16} aria-hidden="true" />
            <span className="rr-sr">Menu</span>
          </button>
          <Link to={sectionHref("/workspace", repositoryId)} className="rr-brand">RenderRepo</Link>
          <div className="rr-repo-switch">
            <button
              type="button"
              ref={repoButtonRef}
              aria-haspopup="listbox"
              aria-expanded={repoOpen}
              aria-label={`Codebase ${selectorLabel}`}
              onClick={() => setRepoOpen((open) => !open)}
            >
              <strong>{selectorLabel}</strong>
            </button>
            {repoOpen ? (
              <div className="rr-repo-menu" role="listbox" aria-label="Codebases" ref={repoMenuRef}>
                {repositories.length > 5 ? (
                  <input
                    autoFocus
                    aria-label="Find a codebase"
                    value={repoQuery}
                    placeholder="Find a codebase"
                    onChange={(event) => setRepoQuery(event.target.value)}
                  />
                ) : null}
                {allowAllCodebases ? (
                  <button type="button" role="option" aria-selected={!repositoryId} onClick={() => chooseRepository("")}>
                    All codebases
                  </button>
                ) : null}
                {visibleRepos.map((repo) => (
                  <button
                    key={repo._id}
                    type="button"
                    role="option"
                    aria-selected={String(repo._id) === String(repositoryId)}
                    onClick={() => chooseRepository(String(repo._id))}
                  >
                    {repoLabel(repo)}
                  </button>
                ))}
                {!repositories.length ? <p className="rr-muted">No codebases connected yet.</p> : null}
                {repositories.length && !visibleRepos.length ? <p className="rr-muted">No matching codebase.</p> : null}
                <Link to="/settings#repositories" onClick={closeRepos}>Connect a codebase</Link>
              </div>
            ) : null}
          </div>
          <div className="rr-launch-wrap">
            <form className="rr-top-launch" onSubmit={submitLauncher}>
              <label className="rr-sr" htmlFor="top-pr-url">Pull request URL</label>
              <input
                id="top-pr-url"
                value={prUrl}
                placeholder="Paste a pull request URL…"
                aria-invalid={Boolean(prError)}
                onChange={(event) => {
                  setPrUrl(event.target.value);
                  setPrError("");
                }}
              />
              <button type="submit" className="rr-button">Review</button>
            </form>
            {prError ? <p className="rr-top-error" role="alert">{prError}</p> : null}
          </div>
          <button type="button" className="rr-button rr-top-launch-compact" ref={reviewButtonRef} onClick={() => openReview("")}>Review pull request</button>
          <details className="rr-account">
            <summary>
              <span className="rr-avatar" aria-hidden="true">{accountInitial(user)}</span>
              <span className="rr-sr">Account</span>
            </summary>
            <div className="rr-account-menu">
              <Link to="/settings#repositories">Codebases</Link>
              <Link to="/settings#github">GitHub connection</Link>
              <Link to="/settings#billing">Billing</Link>
              <Link to="/settings#reviews">Review settings</Link>
              <Link to="/settings#account">Account</Link>
              {EXPERIMENTAL ? <Link to="/settings#experimental">Experimental features</Link> : null}
              {user?.isAdmin ? <Link to="/admin">Admin</Link> : null}
              <button type="button" className="rr-text-button" onClick={onLogout}>Log out</button>
            </div>
          </details>
        </header>
        {reviewOpen ? (
          <Dialog title="Review a pull request" onClose={closeReview}>
            <StartDecisionReview
              key={reviewSeed || "blank"}
              embedded
              quiet
              initialUrl={reviewSeed}
              autoPreview={Boolean(reviewSeed)}
              mutateAllowed={mutateAllowed}
              onStarted={(review) => {
                setReviewOpen(false);
                if (review?.id) navigate(`/reviews/${review.id}`);
              }}
            />
          </Dialog>
        ) : null}
        <div className="rr-frame">
          <aside id="primary-navigation" className={`rr-sidebar${navOpen ? " is-open" : ""}`}>
            <nav className="rr-nav" aria-label="Primary">
              {NAV.map((item) => (
                <Link key={item.kind} to={sectionHref(item.href, repositoryId)} aria-current={navCurrent(area, item)}>
                  <Icon name={item.kind} />
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="rr-sidebar-foot">
              <nav className="rr-nav" aria-label="Workspace">
                <Link to={sectionHref("/settings", repositoryId)} aria-current={area === "settings" ? "page" : undefined}>
                  <Settings size={16} aria-hidden="true" />
                  Settings
                </Link>
              </nav>
            </div>
          </aside>
          <main id="main-content" className="rr-main" tabIndex={-1}>
            <RouteFocusManager pathname={pathname} />
            {children}
          </main>
        </div>
      </div>
    </ReviewLaunchContext.Provider>
  );
}
